import {
  deriveEffectiveTimeFrame,
  isContinuousScheduleType,
  type AvailabilityTimeFrame,
  type ScheduleType,
} from '@kouto/types';
import { DiscountType } from '@kouto/cart-prices';
import { ICartExperience } from 'types/cart';
import {
  CartPrice,
  CartPriceBooking,
  CartPriceBookingInput,
} from 'types/cartPricing';
import { convertIntervalToMinutes } from 'utils/formatDuration';

export type ServerCartQuote = {
  /** What each cart item costs before tax, add-ons included, keyed by cart item id. */
  lineSubtotals: Record<string, number>;
  /** What each cart item costs after tax, add-ons included, keyed by cart item id. */
  lineTotals: Record<string, number>;
  lines: Record<string, CartPriceBooking>;
  subtotal: number;
  taxAmount: number;
  /** Tax levied across every quoted line, keyed by tax id. */
  taxBreakdown: Record<string, number>;
  total: number;
};

/**
 * Where an add-on's tax lands in `taxBreakdown`: the endpoint reports an add-on's tax as one figure
 * with no ids, so it cannot be attributed to a named tax.
 */
export const UNATTRIBUTED_TAX_ID = 'unattributed';

/**
 * The shape a listing needs for how it is booked to be read. The public V3 listing says at the top level
 * whether its collection is scheduled, flexible or multi-day, and each resource group's availability rule
 * says how that group in particular is counted — by the hour, the day or the night.
 */
export type TimeFrameListing = {
  resourceGroups?:
    | {
        availabilityRule?: { timeFrame: AvailabilityTimeFrame } | null;
        id: string;
      }[]
    | null;
  scheduleType?: ScheduleType | null;
};

export type QuotedPriceTier = {
  amount: number;
  isPricedPerDuration: boolean;
  name: string;
  /** The rate every slice of the tier shared, or null when the rate changed by date. */
  pricePerUnit: number | null;
  quantity: number;
  units: number;
};

const LOCAL_DATE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})(:\d{2})?/;

/** The `YYYY-MM-DDTHH:mm:ss` local form the endpoint parses, whatever precision the cart stored. */
export const toLocalDateTimeString = (value: string): string => {
  const match = value.match(LOCAL_DATE_TIME_PATTERN);
  if (!match) {
    return value;
  }
  return `${match[1]}${match[2] ?? ':00'}`;
};

/**
 * The frame the item's group is counted in. The item's own group first: a collection can hold a nightly
 * group beside a daily one. When the item names no group the listing has, the first group with a rule
 * stands in, so a one-group collection still reads. Null for a scheduled listing, whose groups carry no rule —
 * whatever rule one still holds from before the listing was switched.
 */
export const resolveListingTimeFrame = (
  listing: TimeFrameListing | null | undefined,
  groupId?: string,
): AvailabilityTimeFrame | null => {
  if (!listing) {
    return null;
  }
  const groups = listing.resourceGroups ?? [];
  const group =
    groups.find((candidate) => candidate.id === groupId) ??
    groups.find((candidate) => candidate.availabilityRule);
  const ruleTimeFrame = group?.availabilityRule?.timeFrame ?? null;
  // The collection says whether the listing is booked continuously at all; a listing that does not
  // carry its type is left to the rule.
  return listing.scheduleType
    ? deriveEffectiveTimeFrame(listing.scheduleType, ruleTimeFrame)
    : ruleTimeFrame;
};

/**
 * Whether the item is booked continuously — by the hour, the day or the night — rather than by session.
 * The listing's schedule type is the authority; a listing that does not carry one is read from whether
 * its groups have a rule to be counted by at all.
 */
export const isContinuousCartItem = (
  item: Pick<ICartExperience, 'groupId'>,
  listing: TimeFrameListing | null | undefined,
): boolean =>
  listing?.scheduleType
    ? isContinuousScheduleType(listing.scheduleType)
    : resolveListingTimeFrame(listing, item.groupId) !== null;

/** The coupon fields that decide which items it reaches. */
export type QuotedCoupon = {
  discountType: string;
  experienceIds: readonly string[];
  type: string;
};

/**
 * The items the server prices: every continuous item, and — once an amount coupon reaches one of them —
 * every other item that coupon reaches. An amount coupon is spent once across the cart, split by what
 * each item is worth, so it must be priced in one place: half on the quote and half locally would each
 * take the full amount.
 */
export const selectQuotedCartItems = (
  cartItems: Record<string, ICartExperience>,
  listings: Record<string, TimeFrameListing>,
  coupon?: QuotedCoupon | null,
): Record<string, ICartExperience> => {
  const items = Object.values(cartItems);
  const isContinuous = (item: ICartExperience) =>
    isContinuousCartItem(item, listings[item.listingId || item.experienceId]);
  const reachedByAmountCoupon = (item: ICartExperience) =>
    coupon?.discountType === DiscountType.COUPON &&
    coupon.type === 'amount' &&
    coupon.experienceIds.includes(item.experienceId);
  const sharesAmountCoupon = items.some(
    (item) => isContinuous(item) && reachedByAmountCoupon(item),
  );

  return items
    .filter(
      (item) =>
        isContinuous(item) ||
        (sharesAmountCoupon && reachedByAmountCoupon(item)),
    )
    .reduce<Record<string, ICartExperience>>(
      (quoted, item) => ({ ...quoted, [item.cartItemId]: item }),
      {},
    );
};

/**
 * The cart as the cart-price endpoint wants it: one line per cart item, carrying every tier the item
 * holds, so the item's flat fees are levied once, as the charge will levy them. Each line is keyed by
 * the item's id so the answer can be read back per item.
 */
export const buildQuoteBookings = (
  cartItems: Record<string, ICartExperience>,
): CartPriceBookingInput[] =>
  Object.values(cartItems).map((item) => {
    const quantityByTier = item.participants.reduce<Record<string, number>>(
      (counts, participant) => ({
        ...counts,
        [participant.priceTierName]:
          (counts[participant.priceTierName] ?? 0) + 1,
      }),
      {},
    );

    return {
      durationMinutes: Math.round(
        convertIntervalToMinutes(item.sessionDuration),
      ),
      experienceId: item.experienceId,
      lineId: item.cartItemId,
      listingAddOns: (item.addOns ?? [])
        .filter((addOn) => addOn.selectedNumber > 0)
        .map((addOn) => ({
          listingAddOnId: addOn.id,
          quantity: addOn.selectedNumber,
        })),
      priceTiers: Object.entries(quantityByTier).map(
        ([priceTierName, quantity]) => ({ priceTierName, quantity }),
      ),
      startDateTime: toLocalDateTimeString(item.sessionDateTime),
    };
  });

const sumAddOns = (
  booking: CartPriceBooking,
  field: 'subtotal' | 'taxAmount' | 'total',
): number =>
  booking.listingAddOns.reduce((sum, addOn) => sum + addOn[field], 0);

/** A line's tax by id, with whatever the ids do not account for filed under `UNATTRIBUTED_TAX_ID`. */
export const deriveLineTaxBreakdown = (
  booking: CartPriceBooking,
): Record<string, number> => {
  const attributed = booking.taxBreakdown.reduce<Record<string, number>>(
    (acc, entry) => ({
      ...acc,
      [entry.id]: (acc[entry.id] ?? 0) + entry.computedAmount,
    }),
    {},
  );
  const attributedAmount = Object.values(attributed).reduce(
    (sum, amount) => sum + amount,
    0,
  );
  const unattributed =
    booking.taxAmount + sumAddOns(booking, 'taxAmount') - attributedAmount;

  if (unattributed <= 0.000001) {
    return attributed;
  }
  return {
    ...attributed,
    [UNATTRIBUTED_TAX_ID]:
      (attributed[UNATTRIBUTED_TAX_ID] ?? 0) + unattributed,
  };
};

/** A quoted line's slices regrouped by tier, so each tier can be shown as one amount. */
export const groupPricedUnitsByTier = (
  booking: CartPriceBooking,
): QuotedPriceTier[] =>
  booking.pricedUnits.reduce<QuotedPriceTier[]>((tiers, unit) => {
    const existing = tiers.find((tier) => tier.name === unit.priceTierName);
    if (!existing) {
      return [
        ...tiers,
        {
          amount: unit.amount,
          isPricedPerDuration: unit.isPricedPerDuration,
          name: unit.priceTierName,
          pricePerUnit: unit.pricePerUnit,
          quantity: unit.quantity,
          units: unit.units,
        },
      ];
    }
    return tiers.map((tier) =>
      tier === existing
        ? {
            ...tier,
            amount: tier.amount + unit.amount,
            isPricedPerDuration:
              tier.isPricedPerDuration || unit.isPricedPerDuration,
            pricePerUnit:
              tier.pricePerUnit === unit.pricePerUnit
                ? tier.pricePerUnit
                : null,
            quantity: Math.max(tier.quantity, unit.quantity),
            units: tier.units + unit.units,
          }
        : tier,
    );
  }, []);

/** What one of each tier costs for the whole booking a line quotes, by tier name. */
export const getStayPricesByTier = (
  booking: CartPriceBooking,
): Record<string, number> =>
  Object.fromEntries(
    groupPricedUnitsByTier(booking).map((tier) => [
      tier.name,
      tier.amount / Math.max(1, tier.quantity),
    ]),
  );

/** The endpoint's answer folded back onto the cart items it was asked about. */
export const summariseQuote = (cart: CartPrice): ServerCartQuote => {
  const identifiedLines = cart.bookings.filter(
    (booking): booking is CartPriceBooking & { lineId: string } =>
      booking.lineId !== null,
  );

  return {
    lineSubtotals: identifiedLines.reduce<Record<string, number>>(
      (totals, booking) => ({
        ...totals,
        [booking.lineId]:
          (totals[booking.lineId] ?? 0) +
          booking.subtotal +
          sumAddOns(booking, 'subtotal'),
      }),
      {},
    ),
    lineTotals: identifiedLines.reduce<Record<string, number>>(
      (totals, booking) => ({
        ...totals,
        [booking.lineId]:
          (totals[booking.lineId] ?? 0) +
          booking.total +
          sumAddOns(booking, 'total'),
      }),
      {},
    ),
    lines: identifiedLines.reduce<Record<string, CartPriceBooking>>(
      (lines, booking) => ({ ...lines, [booking.lineId]: booking }),
      {},
    ),
    subtotal: cart.subtotal,
    taxAmount: cart.taxAmount,
    taxBreakdown: cart.bookings.reduce<Record<string, number>>(
      (breakdown, booking) =>
        Object.entries(deriveLineTaxBreakdown(booking)).reduce(
          (acc, [taxId, amount]) => ({
            ...acc,
            [taxId]: (acc[taxId] ?? 0) + amount,
          }),
          breakdown,
        ),
      {},
    ),
    total: cart.total,
  };
};
